import { chartEditorContract } from "@common/contracts/chart-editor.contract";
import {
    ChartEditorAddAccountBody,
    chartEditorAddAccountResponseSchema,
    ChartEditorAddGroupBody,
    ChartEditorCreatedAccount,
    ChartEditorDeleteBody,
    ChartEditorMoveBody,
    ChartEditorRenameGroupBody,
    ChartEditorSetAccountDisabledBody,
    ChartEditorUpdateAccountBody,
} from "@common/schemas/domains/accounting/chart-editor.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";

const client = getBackendAPIClient(chartEditorContract);

// The executor toasts once for the whole queue and then recovers from the
// server, so the interceptor must not also toast the body message.
const extraHeaders = { [SUPPRESS_ERROR_TOAST_HEADER]: "true" };

// One queued gesture, already translated off the editor's draft ids.
export type ChartEditorWireOperation =
    | { type: "updateAccount"; body: ChartEditorUpdateAccountBody }
    | { type: "addAccount"; body: ChartEditorAddAccountBody }
    | { type: "addGroup"; body: ChartEditorAddGroupBody }
    | { type: "renameGroup"; body: ChartEditorRenameGroupBody }
    | { type: "deleteRows"; body: ChartEditorDeleteBody }
    | { type: "setAccountDisabled"; body: ChartEditorSetAccountDisabledBody }
    | { type: "move"; body: ChartEditorMoveBody };

// Only a create answers with rows the client must keep; every other operation
// resolves empty so the caller can treat the queue uniformly.
export async function sendChartEditorOperation({
    workspaceId,
    operation,
}: {
    workspaceId: string;
    operation: ChartEditorWireOperation;
}): Promise<ChartEditorCreatedAccount[]> {
    const params = { workspaceId };
    switch (operation.type) {
        case "updateAccount":
            await client.updateAccount({
                params,
                body: operation.body,
                extraHeaders,
            });
            return [];
        case "addAccount": {
            const response = await client.addAccount({
                params,
                body: operation.body,
                extraHeaders,
            });
            return chartEditorAddAccountResponseSchema.parse(response.body)
                .accounts;
        }
        case "addGroup":
            await client.addGroup({
                params,
                body: operation.body,
                extraHeaders,
            });
            return [];
        case "renameGroup":
            await client.renameGroup({
                params,
                body: operation.body,
                extraHeaders,
            });
            return [];
        case "deleteRows":
            await client.deleteRows({
                params,
                body: operation.body,
                extraHeaders,
            });
            return [];
        case "setAccountDisabled":
            await client.setAccountDisabled({
                params,
                body: operation.body,
                extraHeaders,
            });
            return [];
        case "move":
            await client.move({ params, body: operation.body, extraHeaders });
            return [];
    }
}
